<template>
  <GroupItemDetailsTable
    v-if="category == 'material' && selectedGroupItem"
    :list="groupItemsDetails"
    :total-records="groupItemsDetailsTotalCount"
    :headers="groupItemsMaterialHeaders"
    table-id="groupItemsMaterial"
    @add-new="onAddMaterials"
    @delete="onDeleteGroupItemMaterial"
    @deleteMultiselect="onDeleteMultiselectGroupItemMaterial"
    @refresh-table="onRefreshDetailTable" />

  <GroupItemDetailsTable
    v-if="category == 'alterCode' && selectedGroupItem"
    :list="groupItemsDetails"
    :total-records="groupItemsDetailsTotalCount"
    :headers="groupItemsAlterCodeHeaders"
    table-id="groupItemsAlterCode"
    @add-new="onAddAlterCodes"
    @delete="onDeleteGroupItemAlterCode"
    @deleteMultiselect="onDeleteMultiselectGroupItemAlterCode"
    @refresh-table="onRefreshDetailTable" />

  <GroupItemDetailsTable
    v-if="category == 'customer' && selectedGroupItem"
    :list="groupItemsDetails"
    :total-records="groupItemsDetailsTotalCount"
    :headers="groupItemsCustomerHeaders"
    table-id="groupItemsCustomer"
    @add-new="onAddCustomers"
    @delete="onDeleteGroupItemCustomer"
    @deleteMultiselect="onDeleteMultiselectGroupItemCustomer"
    @refresh-table="onRefreshDetailTable" />

  <GroupItemDetailsTable
    v-if="category == 'traderSite' && selectedGroupItem"
    :list="groupItemsDetails"
    :total-records="groupItemsDetailsTotalCount"
    :headers="groupItemsTraderSiteHeaders"
    table-id="groupItemsTraderSite"
    @add-new="onAddTraderSites"
    @delete="onDeleteGroupItemTraderSite"
    @deleteMultiselect="onDeleteMultiselectGroupItemTraderSite"
    @refresh-table="onRefreshDetailTable" />

  <GroupItemDetailsModalAdd
    v-if="isModalAddActive"
    :list="detailsList"
    :total-records="detailsListCount"
    :category="category"
    @close="isModalAddActive = false"
    @refresh-data="onRefreshModalData"
    @multi-select="onMultiselectItems" />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useGroupingStore } from '@/stores/grouping';
import { getMaterialsWithConditions } from '@/services/api/materials';
import { getAlterCodesWithConditions } from '@/services/api/alterCodes';
import { getCustomersWithConditions, getTraderSitesWithConditions } from '@/services/api/traders';
import { v4 as uuidv4 } from 'uuid';

import GroupItemDetailsTable from './GroupItemDetailsTable.vue';
import GroupItemDetailsModalAdd from './GroupItemDetailsModalAdd.vue';
import type { AlterCode, Material } from '@/types/Materials';
import type { Customer, TraderSite } from '@/types/Traders';
import { RecordMode, type TableFilterData } from '@/types/Base';
import type {
  GroupItem,
  GroupItemAlterCode,
  GroupItemCustomer,
  GroupItemDetail,
  GroupItemMaterial,
  GroupItemTraderSite,
} from '@/types/Groups';

const {
  selectedGroupItem,
  selectedGroup,
  category,
  mode,
  modifiedGroups,
  groupItemsDetails,
  groupItemsDetailsTotalCount,
} = storeToRefs(useGroupingStore());

const { getGroupItemsTableData } = useGroupingStore();

const groupItemsTraderSiteHeaders = computed(() => [
  { value: 'traderSite.name', text: 'Name', type: 'text', active: true },
  { value: 'traderSite.code', text: 'Code', type: 'text', active: true },
  {
    value: 'deleted',
    text: 'Status',
    type: 'status',
    active: true,
    cellStatusColor: 'red',
    cellStatusActiveText: 'Deleted',
    isSortHidden: true,
  },
  {
    value: 'tradeSite.deleted',
    text: 'TraderSite Status',
    type: 'status',
    cellStatusColor: 'red',
    cellStatusActiveText: 'Deleted',
    active: true,
    isSortHidden: true,
  },
  { value: 'actions', text: 'Actions', active: mode.value === 'edit' },
]);

const groupItemsMaterialHeaders = computed(() => [
  { value: 'material.name', text: 'Name', type: 'text', active: true },
  { value: 'material.code', text: 'Code', type: 'text', active: true },
  {
    value: 'deleted',
    text: 'Status',
    type: 'status',
    active: true,
    cellStatusColor: 'red',
    cellStatusActiveText: 'Deleted',
    isSortHidden: true,
  },
  {
    value: 'material.deleted',
    text: 'Material Status',
    type: 'status',
    cellStatusColor: 'red',
    cellStatusActiveText: 'Deleted',
    active: true,
    isSortHidden: true,
  },
  { value: 'actions', text: 'Actions', active: mode.value === 'edit' },
]);

const groupItemsCustomerHeaders = computed(() => [
  { value: 'customer.trader.name', text: 'Name', type: 'text', active: true },
  { value: 'customer.code', text: 'Code', type: 'text', active: true },
  {
    value: 'deleted',
    text: 'Status',
    type: 'status',
    active: true,
    cellStatusColor: 'red',
    isSortHidden: true,
    cellStatusActiveText: 'Deleted',
  },
  {
    value: 'customer.deleted',
    text: 'Customer Status',
    type: 'status',
    cellStatusColor: 'red',
    cellStatusActiveText: 'Deleted',
    active: true,
    isSortHidden: true,
  },
  { value: 'actions', text: 'Actions', active: mode.value === 'edit' },
]);

const groupItemsAlterCodeHeaders = computed(() => [
  { value: 'alterCode.name', text: 'Name', type: 'text', active: true },
  { value: 'alterCode.code', text: 'Code', type: 'text', active: true },
  {
    value: 'deleted',
    text: 'Status',
    type: 'status',
    active: true,
    cellStatusColor: 'red',
    cellStatusActiveText: 'Deleted',
    isSortHidden: true,
  },
  {
    value: 'alterCode.deleted',
    text: 'AlterCode Status',
    type: 'status',
    cellStatusColor: 'red',
    cellStatusActiveText: 'Deleted',
    active: true,
    isSortHidden: true,
  },
  { value: 'actions', text: 'Actions', active: mode.value === 'edit' },
]);

const detailsList = ref<Material[] | TraderSite[] | Customer[] | AlterCode[]>([]);
const detailsListCount = ref<number>();
const isModalAddActive = ref(false);

async function onRefreshModalData(data: TableFilterData) {
  switch (category.value) {
    case 'material':
      await onAddMaterials(data);
      break;
    case 'alterCode':
      await onAddAlterCodes(data);
      break;
    case 'customer':
      await onAddCustomers(data);
      break;
    case 'traderSite':
      await onAddTraderSites(data);
      break;
    default:
      break;
  }
}

async function onAddMaterials(data: TableFilterData | undefined) {
  detailsList.value = [];
  isModalAddActive.value = true;
  const res = await getMaterialsWithConditions({
    groupItemId: selectedGroupItem.value?.id,
    itemsPerPage: data?.itemsPerPage || 50,
    query: data?.query,
    page: data?.page || 1,
    sortColumn: data?.sortColumn || '',
    sortAscending: data?.order === 'asc',
    includeDeleted: false,
  });
  if (res) {
    detailsList.value = filterDetailsList(res.data);
    detailsListCount.value = res.count;
  }
}

async function onAddTraderSites(data: TableFilterData | undefined) {
  detailsList.value = [];
  isModalAddActive.value = true;
  const res = await getTraderSitesWithConditions({
    groupItemId: selectedGroupItem.value?.id,
    itemsPerPage: data?.itemsPerPage || 50,
    query: data?.query,
    page: data?.page || 1,
    sortColumn: data?.sortColumn || '',
    sortAscending: data?.order === 'asc',
    isActive: true,
  });
  if (res) {
    detailsList.value = filterDetailsList(res.data);
    detailsListCount.value = res.count;
  }
}

async function onAddCustomers(data: TableFilterData | undefined) {
  detailsList.value = [];
  isModalAddActive.value = true;
  const res = await getCustomersWithConditions({
    groupItemId: selectedGroupItem.value?.id,
    itemsPerPage: data?.itemsPerPage || 50,
    query: data?.query,
    page: data?.page || 1,
    sortColumn: data?.sortColumn || '',
    sortAscending: data?.order === 'asc',
  });

  if (res) {
    detailsList.value = filterDetailsList(res.data);
    detailsListCount.value = res.count;
  }
}

async function onAddAlterCodes(data: TableFilterData | undefined) {
  detailsList.value = [];
  isModalAddActive.value = true;
  const res = await getAlterCodesWithConditions({
    groupItemId: selectedGroupItem.value?.id,
    itemsPerPage: data?.itemsPerPage || 50,
    query: data?.query,
    page: data?.page || 1,
    sortColumn: data?.sortColumn || '',
    sortAscending: data?.order === 'asc',
  });

  if (res) {
    detailsList.value = filterDetailsList(res.data);
    detailsListCount.value = res.count;
  }
}

function filterDetailsList(
  resData: Material[] | TraderSite[] | Customer[] | AlterCode[]
): Material[] | TraderSite[] | Customer[] | AlterCode[] {
  const materialIdsToExclude = new Set<string>();
  const traderSiteIdsToExclude = new Set<string>();
  const alterCodeIdsToExclude = new Set<string>();
  const customerIdsToExclude = new Set<string>();

  modifiedGroups.value.forEach((group) => {
    if (!group.groupItems) return;
    group.groupItems.forEach((groupItem) => {
      if (groupItem.groupItemMaterials) {
        groupItem.groupItemMaterials.forEach((item) => {
          if (item.materialId && item.recordMode !== RecordMode.Deleted) {
            materialIdsToExclude.add(item.materialId);
          }
        });
      }
      if (groupItem.groupItemTraderSites) {
        groupItem.groupItemTraderSites.forEach((item) => {
          if (item.traderSiteId && item.recordMode !== RecordMode.Deleted) {
            traderSiteIdsToExclude.add(item.traderSiteId);
          }
        });
      }
      if (groupItem.groupItemAlterCodes) {
        groupItem.groupItemAlterCodes.forEach((item) => {
          if (item.alterCodeId && item.recordMode !== RecordMode.Deleted) {
            alterCodeIdsToExclude.add(item.alterCodeId);
          }
        });
      }
      if (groupItem.groupItemCustomers) {
        groupItem.groupItemCustomers.forEach((item) => {
          if (item.customerId && item.recordMode !== RecordMode.Deleted) {
            customerIdsToExclude.add(item.customerId);
          }
        });
      }
    });
  });

  return resData.filter((item) => {
    if ((item as Material).id && materialIdsToExclude.has((item as Material).id)) {
      return false;
    } else if ((item as TraderSite).id && traderSiteIdsToExclude.has((item as TraderSite).id)) {
      return false;
    } else if ((item as AlterCode).id && alterCodeIdsToExclude.has((item as AlterCode).id)) {
      return false;
    } else if ((item as Customer).id && customerIdsToExclude.has((item as Customer).id)) {
      return false;
    }
    return true;
  }) as Material[] | TraderSite[] | Customer[] | AlterCode[];
}

const selectedGroupItemDetails = ref<any>([]);

function onMultiselectItems(items: Material[] | TraderSite[] | Customer[] | AlterCode[]) {
  isModalAddActive.value = false;
  selectedGroupItemDetails.value.push(...items);
  switch (category.value) {
    case 'material':
      onCreateGroupItemMaterial(items as Material[]);
      break;

    case 'alterCode':
      onCreateGroupItemAlterCode(items as AlterCode[]);
      break;

    case 'customer':
      onCreateGroupItemCustomer(items as Customer[]);
      break;

    case 'traderSite':
      onCreateGroupItemTraderSite(items as TraderSite[]);
      break;

    default:
      break;
  }
}

async function onCreateGroupItemMaterial(items: Material[]) {
  if (!selectedGroup.value || !selectedGroupItem.value) return;

  const groupId = selectedGroup.value.id;
  let groupInModified = modifiedGroups.value.find((group) => group.id === groupId);

  if (!groupInModified) {
    selectedGroup.value.recordMode = RecordMode.Modified;
    if (!selectedGroup.value.groupItems) {
      selectedGroup.value.groupItems = [];
    }
    modifiedGroups.value.push(selectedGroup.value);
    groupInModified = selectedGroup.value;
  }
  if (!groupInModified.groupItems) {
    groupInModified.groupItems = [];
  }
  let groupItemInModified = groupInModified.groupItems.find(
    (item) => item.id === selectedGroupItem.value!.id
  );

  if (!groupItemInModified) {
    selectedGroupItem.value.recordMode = RecordMode.Modified;
    if (!selectedGroupItem.value.groupItemMaterials) {
      selectedGroupItem.value.groupItemMaterials = [];
    }

    groupInModified.groupItems.push(selectedGroupItem.value);
    groupItemInModified = selectedGroupItem.value;
  }
  if (!groupItemInModified.groupItemMaterials) {
    groupItemInModified.groupItemMaterials = [];
  }
  items.forEach((item) => {
    const newGIMaterial: GroupItemMaterial = {
      id: uuidv4(),
      isActive: true,
      isFromManager: true,
      groupItemId: selectedGroupItem.value!.id,
      mwAccountGroupId: selectedGroupItem.value!.mwAccountGroupId || '',
      materialId: item.id,
      material: item,
      recordMode: RecordMode.Added,
      itemType: 'material',
    };

    if (!groupItemInModified.groupItemMaterials) {
      groupItemInModified.groupItemMaterials = [];
    }

    groupItemInModified.groupItemMaterials.push(newGIMaterial);
  });

  await onRefreshDetailTable({ page: 1, itemsPerPage: 10 }, true);
}

async function onCreateGroupItemTraderSite(items: TraderSite[]) {
  if (!selectedGroup.value || !selectedGroupItem.value) return;
  const groupId = selectedGroup.value.id;
  let groupInModified = modifiedGroups.value.find((group) => group.id === groupId);

  if (!groupInModified) {
    selectedGroup.value.recordMode = RecordMode.Modified;
    if (!selectedGroup.value.groupItems) {
      selectedGroup.value.groupItems = [];
    }
    modifiedGroups.value.push(selectedGroup.value);
    groupInModified = selectedGroup.value;
  }
  if (!groupInModified.groupItems) {
    groupInModified.groupItems = [];
  }
  let groupItemInModified = groupInModified.groupItems.find(
    (item) => item.id === selectedGroupItem.value!.id
  );

  if (!groupItemInModified) {
    selectedGroupItem.value.recordMode = RecordMode.Modified;
    if (!selectedGroupItem.value.groupItemTraderSites) {
      selectedGroupItem.value.groupItemTraderSites = [];
    }
    groupInModified.groupItems.push(selectedGroupItem.value);

    groupItemInModified = selectedGroupItem.value;
  }
  if (!groupItemInModified.groupItemTraderSites) {
    groupItemInModified.groupItemTraderSites = [];
  }
  items.forEach((item) => {
    const newGITraderSite: GroupItemTraderSite = {
      id: uuidv4(),
      isActive: true,
      isFromManager: true,
      groupItemId: selectedGroupItem.value!.id,
      mwAccountGroupId: selectedGroupItem.value!.mwAccountGroupId || '',
      traderSiteId: item.id,
      traderSite: item,
      recordMode: RecordMode.Added,
      itemType: 'traderSite',
    };

    if (!groupItemInModified.groupItemTraderSites) {
      groupItemInModified.groupItemTraderSites = [];
    }

    groupItemInModified.groupItemTraderSites.push(newGITraderSite);
  });

  await onRefreshDetailTable({ page: 1, itemsPerPage: 10 }, true);
}

async function onCreateGroupItemAlterCode(items: AlterCode[]) {
  if (!selectedGroup.value || !selectedGroupItem.value) return;
  const groupId = selectedGroup.value.id;
  let groupInModified = modifiedGroups.value.find((group) => group.id === groupId);

  if (!groupInModified) {
    selectedGroup.value.recordMode = RecordMode.Modified;
    if (!selectedGroup.value.groupItems) {
      selectedGroup.value.groupItems = [];
    }
    modifiedGroups.value.push(selectedGroup.value);
    groupInModified = selectedGroup.value;
  }
  if (!groupInModified.groupItems) {
    groupInModified.groupItems = [];
  }
  let groupItemInModified = groupInModified.groupItems.find(
    (item) => item.id === selectedGroupItem.value!.id
  );

  if (!groupItemInModified) {
    selectedGroupItem.value.recordMode = RecordMode.Modified;
    if (!selectedGroupItem.value.groupItemAlterCodes) {
      selectedGroupItem.value.groupItemAlterCodes = [];
    }

    groupInModified.groupItems.push(selectedGroupItem.value);
    groupItemInModified = selectedGroupItem.value;
  }
  if (!groupItemInModified.groupItemAlterCodes) {
    groupItemInModified.groupItemAlterCodes = [];
  }
  items.forEach((item) => {
    const newGIAlterCode: GroupItemAlterCode = {
      id: uuidv4(),
      isActive: true,
      isFromManager: true,
      groupItemId: selectedGroupItem.value!.id,
      mwAccountGroupId: selectedGroupItem.value!.mwAccountGroupId || '',
      alterCodeId: item.id,
      alterCode: item,
      recordMode: RecordMode.Added,
      itemType: 'alterCode',
    };

    if (!groupItemInModified.groupItemAlterCodes) {
      groupItemInModified.groupItemAlterCodes = [];
    }

    groupItemInModified.groupItemAlterCodes.push(newGIAlterCode);
  });

  await onRefreshDetailTable({ page: 1, itemsPerPage: 10 }, true);
}

async function onCreateGroupItemCustomer(items: Customer[]) {
  if (!selectedGroup.value || !selectedGroupItem.value) return;
  const groupId = selectedGroup.value.id;
  let groupInModified = modifiedGroups.value.find((group) => group.id === groupId);

  if (!groupInModified) {
    selectedGroup.value.recordMode = RecordMode.Modified;
    if (!selectedGroup.value.groupItems) {
      selectedGroup.value.groupItems = [];
    }
    modifiedGroups.value.push(selectedGroup.value);
    groupInModified = selectedGroup.value;
  }
  if (!groupInModified.groupItems) {
    groupInModified.groupItems = [];
  }
  let groupItemInModified = groupInModified.groupItems.find(
    (item) => item.id === selectedGroupItem.value!.id
  );

  if (!groupItemInModified) {
    selectedGroupItem.value.recordMode = RecordMode.Modified;
    if (!selectedGroupItem.value.groupItemCustomers) {
      selectedGroupItem.value.groupItemCustomers = [];
    }

    groupInModified.groupItems.push(selectedGroupItem.value);
    groupItemInModified = selectedGroupItem.value;
  }
  if (!groupItemInModified.groupItemCustomers) {
    groupItemInModified.groupItemCustomers = [];
  }
  items.forEach((item) => {
    const newGICustomer: GroupItemCustomer = {
      id: uuidv4(),
      isActive: true,
      isFromManager: true,
      groupItemId: selectedGroupItem.value!.id,
      mwAccountGroupId: selectedGroupItem.value!.mwAccountGroupId || '',
      customerId: item.id,
      customer: item,
      recordMode: RecordMode.Added,
      itemType: 'customer',
    };

    if (!groupItemInModified.groupItemCustomers) {
      groupItemInModified.groupItemCustomers = [];
    }

    groupItemInModified.groupItemCustomers.push(newGICustomer);
  });

  await onRefreshDetailTable({ page: 1, itemsPerPage: 10 }, true);
}

function onDeleteGroupItemMaterial(item: GroupItemMaterial) {
  handleDeleteItem(item, 'groupItemMaterials');
  const index = groupItemsDetails.value.findIndex((i) => i.id === item.id);
  if (index !== -1) {
    if (item.recordMode === RecordMode.Added) {
      groupItemsDetails.value.splice(index, 1);
    } else {
      groupItemsDetails.value[index].deleted = true;
    }
  }
}

function onDeleteGroupItemAlterCode(item: GroupItemAlterCode) {
  handleDeleteItem(item, 'groupItemAlterCodes');
  const index = groupItemsDetails.value.findIndex((i) => i.id === item.id);
  if (index !== -1) {
    if (item.recordMode === RecordMode.Added) {
      groupItemsDetails.value.splice(index, 1);
    } else {
      groupItemsDetails.value[index].deleted = true;
    }
  }
}
function onDeleteGroupItemCustomer(item: GroupItemCustomer) {
  handleDeleteItem(item, 'groupItemCustomers');
  const index = groupItemsDetails.value.findIndex((i) => i.id === item.id);
  if (index !== -1) {
    if (item.recordMode === RecordMode.Added) {
      groupItemsDetails.value.splice(index, 1);
    } else {
      groupItemsDetails.value[index].deleted = true;
    }
  }
}

function onDeleteGroupItemTraderSite(item: GroupItemTraderSite) {
  handleDeleteItem(item, 'groupItemTraderSites');
  const index = groupItemsDetails.value.findIndex((i) => i.id === item.id);
  if (index !== -1) {
    if (item.recordMode === RecordMode.Added) {
      groupItemsDetails.value.splice(index, 1);
    } else {
      groupItemsDetails.value[index].deleted = true;
    }
  }
}

function onDeleteMultiselectGroupItemMaterial(items: GroupItemMaterial[]) {
  // Sort items by index in descending order to avoid index shifting issues when removing
  const sortedItems = items
    .map((item) => ({ item, index: groupItemsDetails.value.findIndex((i) => i.id === item.id) }))
    .filter(({ index }) => index !== -1)
    .sort((a, b) => b.index - a.index);

  sortedItems.forEach(({ item, index }) => {
    handleDeleteItem(item, 'groupItemMaterials');
    if (item.recordMode === RecordMode.Added) {
      groupItemsDetails.value.splice(index, 1);
    } else {
      groupItemsDetails.value[index].deleted = true;
    }
  });
}

function onDeleteMultiselectGroupItemAlterCode(items: GroupItemAlterCode[]) {
  // Sort items by index in descending order to avoid index shifting issues when removing
  const sortedItems = items
    .map((item) => ({ item, index: groupItemsDetails.value.findIndex((i) => i.id === item.id) }))
    .filter(({ index }) => index !== -1)
    .sort((a, b) => b.index - a.index);

  sortedItems.forEach(({ item, index }) => {
    handleDeleteItem(item, 'groupItemAlterCodes');
    if (item.recordMode === RecordMode.Added) {
      groupItemsDetails.value.splice(index, 1);
    } else {
      groupItemsDetails.value[index].deleted = true;
    }
  });
}

function onDeleteMultiselectGroupItemCustomer(items: GroupItemCustomer[]) {
  // Sort items by index in descending order to avoid index shifting issues when removing
  const sortedItems = items
    .map((item) => ({ item, index: groupItemsDetails.value.findIndex((i) => i.id === item.id) }))
    .filter(({ index }) => index !== -1)
    .sort((a, b) => b.index - a.index);

  sortedItems.forEach(({ item, index }) => {
    handleDeleteItem(item, 'groupItemCustomers');
    if (item.recordMode === RecordMode.Added) {
      groupItemsDetails.value.splice(index, 1);
    } else {
      groupItemsDetails.value[index].deleted = true;
    }
  });
}

function onDeleteMultiselectGroupItemTraderSite(items: GroupItemTraderSite[]) {
  // Sort items by index in descending order to avoid index shifting issues when removing
  const sortedItems = items
    .map((item) => ({ item, index: groupItemsDetails.value.findIndex((i) => i.id === item.id) }))
    .filter(({ index }) => index !== -1)
    .sort((a, b) => b.index - a.index);

  sortedItems.forEach(({ item, index }) => {
    handleDeleteItem(item, 'groupItemTraderSites');
    if (item.recordMode === RecordMode.Added) {
      groupItemsDetails.value.splice(index, 1);
    } else {
      groupItemsDetails.value[index].deleted = true;
    }
  });
}

function handleDeleteItem(
  item: GroupItemMaterial | GroupItemAlterCode | GroupItemCustomer | GroupItemTraderSite,
  collectionName: keyof GroupItem
) {
  if (!selectedGroup.value || !selectedGroupItem.value) return;

  const groupId = selectedGroup.value.id;
  let groupInModified = modifiedGroups.value.find((group) => group.id === groupId);

  if (!groupInModified) {
    const groupCopy = { ...selectedGroup.value };
    delete groupCopy.groupItems;
    groupCopy.recordMode = RecordMode.Modified;
    groupCopy.groupItems = [];
    modifiedGroups.value.push(groupCopy);
    groupInModified = groupCopy;
  }

  if (!groupInModified.groupItems) {
    groupInModified.groupItems = [];
  }
  let groupItemInModified = groupInModified.groupItems.find(
    (groupItem) => groupItem.id === selectedGroupItem.value!.id
  );

  if (!groupItemInModified) {
    const groupItemShallowCopy = { ...selectedGroupItem.value };
    delete groupItemShallowCopy.groupItemMaterials;
    delete groupItemShallowCopy.groupItemAlterCodes;
    delete groupItemShallowCopy.groupItemCustomers;
    delete groupItemShallowCopy.groupItemTraderSites;
    groupItemShallowCopy.recordMode = RecordMode.Modified;

    groupItemShallowCopy.groupItemMaterials = [];
    groupItemShallowCopy.groupItemAlterCodes = [];
    groupItemShallowCopy.groupItemCustomers = [];
    groupItemShallowCopy.groupItemTraderSites = [];

    groupInModified.groupItems.push(groupItemShallowCopy);
    groupItemInModified = groupItemShallowCopy;
  }

  let collection: GroupItemDetail[] | undefined = groupItemInModified.groupItemMaterials;

  if (collectionName === 'groupItemMaterials') {
    collection = groupItemInModified.groupItemMaterials;
  } else if (collectionName === 'groupItemAlterCodes') {
    collection = groupItemInModified.groupItemAlterCodes;
  } else if (collectionName === 'groupItemCustomers') {
    collection = groupItemInModified.groupItemCustomers;
  } else if (collectionName === 'groupItemTraderSites') {
    collection = groupItemInModified.groupItemTraderSites;
  } else {
    return;
  }

  if (!collection) return;
  const existingItemIndex = collection.findIndex((collectionItem) => collectionItem.id === item.id);

  if (item.recordMode === RecordMode.Added) {
    if (existingItemIndex !== -1) {
      collection.splice(existingItemIndex, 1);
    }
    if (collection.length === 0) {
      const groupItemIndex = groupInModified.groupItems.findIndex(
        (groupItem) => groupItem.id === groupItemInModified.id
      );
      if (groupItemIndex !== -1) {
        groupInModified.groupItems.splice(groupItemIndex, 1);
      }
    }

    if (groupInModified.groupItems.length === 0) {
      const groupIndex = modifiedGroups.value.findIndex(
        (group) => group.id === groupInModified!.id
      );
      if (groupIndex !== -1) {
        modifiedGroups.value.splice(groupIndex, 1);
      }
    }
  } else {
    const itemCopy = { ...item };
    itemCopy.deleted = true;
    itemCopy.recordMode = RecordMode.Deleted;
    if (existingItemIndex !== -1) {
      collection[existingItemIndex] = itemCopy;
    } else {
      collection.push(itemCopy);
    }
  }
}

async function onRefreshDetailTable(data: TableFilterData, addItems?: boolean) {
  let headers;
  switch (category.value) {
    case 'material':
      headers = groupItemsMaterialHeaders.value;
      break;
    case 'alterCode':
      headers = groupItemsAlterCodeHeaders.value;
      break;
    case 'customer':
      headers = groupItemsCustomerHeaders.value;
      break;
    case 'traderSite':
      headers = groupItemsTraderSiteHeaders.value;
      break;
    default:
      headers = groupItemsMaterialHeaders.value;
  }

  const sortColumn = data.column !== undefined ? headers[data.column]?.value : '';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  await getGroupItemsTableData(
    {
      page: data.page,
      itemsPerPage: data.itemsPerPage,
      query: data.query,
      sortColumn,
      sortAscending,
    },
    addItems
  );
}
</script>
